import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AlbumFormat, AlbumPage, AlbumStyles, AlbumVeil, AlbumPicture } from '../../types';
import { Pattern } from './AlbumPageBackground';
import AlbumPhotoPicker from './AlbumPhotoPicker';
import AlbumFormatPicker from './AlbumFormatPicker';
import AlbumThemePicker from './AlbumThemePicker';
import AlbumFontSelect from './AlbumFontSelect';

interface Props {
  /** `album`: format, theme, type and frames; `page`: this page's background. */
  section: 'album' | 'page';
  styles: AlbumStyles;
  theme: string;
  formats: AlbumFormat[];
  format: string;
  onFormat: (format: string) => void;
  fontPair: string;
  photoFrame: string;
  onAlbumStyle: (style: { fontPair?: string; photoFrame?: string }) => void;
  page: AlbumPage | null;
  photos: AlbumPicture[];
  saving: boolean;
  onTheme: (theme: string) => void;
  onLook: (look: { background?: string | null; backgroundPhotoId?: string | null; backgroundVeil?: AlbumVeil }) => void;
}

const heading = 'text-xs font-semibold uppercase tracking-wider text-slate-500';

/** ADR-0048: the album's theme, and this page's own background — all from the catalogue. */
export default function AlbumStylePanel({ section, styles, theme, formats, format, onFormat, fontPair, photoFrame, onAlbumStyle, page, photos, saving, onTheme, onLook }: Props) {
  const { t } = useTranslation();
  const [pickingPhoto, setPickingPhoto] = useState(false);
  const hasPhoto = !!(page?.backgroundPhotoId || page?.backgroundAlbumPhotoId);

  return (
    <div className="space-y-5">
      {section === 'album' && (
        <>
      <section className="space-y-2">
        <h3 className={heading}>{t('album.format.title')}</h3>
        <AlbumFormatPicker formats={formats} value={format} disabled={saving} onChange={onFormat} />
      </section>

      <section className="space-y-2">
        <h3 className={heading}>{t('album.style.theme')}</h3>
        <AlbumThemePicker styles={styles} value={theme} disabled={saving} onChange={onTheme} />
      </section>

      <section className="space-y-2">
        <h3 className={heading}>{t('album.style.typeface')}</h3>
        <AlbumFontSelect
          label={t('album.style.typeface')}
          choices={styles.fontPairs.map(pair => ({ value: pair.id, label: t(`album.fontPairs.${pair.id}`, { defaultValue: pair.id }), font: pair.title }))}
          value={fontPair}
          disabled={saving}
          onChange={next => { if (next) onAlbumStyle({ fontPair: next }); }}
        />
      </section>

      <section className="space-y-2">
        <h3 className={heading}>{t('album.style.photoFrame')}</h3>
        <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label={t('album.style.photoFrame')}>
          {styles.photoFrames.map(frame => (
            <button
              key={frame}
              role="radio"
              aria-checked={photoFrame === frame}
              disabled={saving}
              onClick={() => onAlbumStyle({ photoFrame: frame })}
              className={`flex flex-col items-center gap-1 rounded-lg py-2 cursor-pointer ${photoFrame === frame ? 'ring-2 ring-teal-500' : 'ring-1 ring-slate-200 dark:ring-slate-700 hover:ring-teal-300'}`}
            >
              <span className={`block w-8 h-6 bg-slate-400 ${
                frame === 'white-border' ? 'ring-[3px] ring-white shadow' : frame === 'rounded' ? 'rounded-md' : frame === 'shadow' ? 'shadow-md' : ''
              }`} />
              <span className="text-[10px] text-slate-500 dark:text-slate-400">{t(`album.photoFrames.${frame}`, { defaultValue: frame })}</span>
            </button>
          ))}
        </div>
      </section>

        </>
      )}

      {section === 'page' && page && (
        <section className="space-y-2">
          <h3 className={heading}>{t('album.style.pageBackground')}</h3>
          <div className="grid grid-cols-6 gap-1.5" role="radiogroup" aria-label={t('album.style.pageBackground')}>
            <button
              role="radio"
              aria-checked={!page.background && !hasPhoto}
              disabled={saving}
              onClick={() => onLook({ background: null, backgroundPhotoId: null })}
              title={t('album.style.fromTheme')}
              className={`aspect-square rounded-md text-[9px] leading-tight text-slate-500 cursor-pointer ${
                !page.background && !hasPhoto ? 'ring-2 ring-teal-500' : 'ring-1 ring-slate-200 dark:ring-slate-700'
              }`}
            >
              {t('album.style.fromTheme')}
            </button>
            {styles.backgrounds.map(bg => {
              const on = page.background === bg.id && !hasPhoto;
              return (
                <button
                  key={bg.id}
                  role="radio"
                  aria-checked={on}
                  aria-label={t(`album.backgrounds.${bg.id}`, { defaultValue: bg.id })}
                  title={t(`album.backgrounds.${bg.id}`, { defaultValue: bg.id })}
                  disabled={saving}
                  onClick={() => onLook({ background: bg.id, backgroundPhotoId: null })}
                  className={`aspect-square rounded-md overflow-hidden cursor-pointer ${on ? 'ring-2 ring-teal-500' : 'ring-1 ring-slate-200 dark:ring-slate-700'}`}
                >
                  <svg viewBox="0 0 24 24" className="w-full h-full" aria-hidden>
                    <rect width={24} height={24} fill={bg.color} />
                    {bg.pattern && <Pattern id={`swatch-${bg.id}`} pattern={bg.pattern} width={24} height={24} />}
                  </svg>
                </button>
              );
            })}
          </div>

          {hasPhoto ? (
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-0.5" role="radiogroup" aria-label={t('album.style.veil')}>
                {(['dark', 'light'] as const).map(veil => (
                  <button
                    key={veil}
                    role="radio"
                    aria-checked={page.backgroundVeil === veil}
                    disabled={saving}
                    onClick={() => onLook({ backgroundVeil: veil })}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer ${
                      page.backgroundVeil === veil ? 'bg-white shadow-sm text-slate-900 dark:bg-slate-700 dark:text-slate-100' : 'text-slate-500'
                    }`}
                  >
                    {t(`album.style.veil_${veil}`)}
                  </button>
                ))}
              </div>
              <button onClick={() => onLook({ backgroundPhotoId: null })} disabled={saving} className="text-xs text-slate-500 hover:text-red-500">
                {t('album.style.removePhoto')}
              </button>
            </div>
          ) : (
            <button onClick={() => setPickingPhoto(v => !v)} className="btn-ghost-sm text-xs">
              {t('album.style.photoBackground')}
            </button>
          )}
          {pickingPhoto && !hasPhoto && (
            <AlbumPhotoPicker
              photos={photos}
              usedIds={new Set()}
              selectedId={null}
              onPick={id => { setPickingPhoto(false); onLook({ backgroundPhotoId: id }); }}
            />
          )}
        </section>
      )}
    </div>
  );
}
